import _refiner from 'refiner-js'
import { ref } from 'vue'

/**
 * useRefiner composable wraps the Refiner.js SDK for Vue 3 usage.
 *
 * Overview:
 * - Initializes Refiner with your project ID and optional locale.
 * - Optionally identifies a user when user info is provided.
 * - Allows showing a Refiner form manually and sending additional metadata.
 * - Supports anonymous feedback when no user info is provided.
 *
 * Usage Patterns:
 * 1. Identified User Feedback
 *    Pass `user` when calling `useRefiner` or when calling `showRefinerForm`.
 *    Example:
 *    ```ts
 *    const { showRefinerForm } = useRefiner({
 *      projectId: 'xxxx-xxxx',
 *      locale: 'en',
 *      user: {
 *        userId: '123',
 *        userName: 'Jane Doe',
 *        userEmail: 'jane@example.com'
 *      }
 *    })
 *
 *    showRefinerForm({
 *      formId: 'yyyy-yyyy',
 *      user: {
 *        userId: '123',
 *        userName: 'Jane Doe',
 *        userEmail: 'jane@example.com'
 *      }
 *    })
 *    ```
 *    → The feedback form will be personalized with this user’s data.
 *
 * 2. Anonymous Feedback
 *    Simply omit the `user` property entirely (do NOT pass `{}` — leave it out).
 *    Example:
 *    ```ts
 *    const { showRefinerForm } = useRefiner({
 *      projectId: 'xxxx-xxxx',
 *      locale: 'en'
 *    })
 *
 *    showRefinerForm({
 *      formId: 'yyyy-yyyy'
 *    })
 *    ```
 *    → No user identification occurs, feedback is collected anonymously.
 *
 * Locale Handling:
 * - The `setLocale` method updates the active language for Refiner forms.
 * - Accepts either a full locale string (e.g. `"en-US"`) or a short code (`"en"`).
 * - If a longer locale is provided, it is trimmed to its first two characters.
 * - ⚠ Important: The locale **must** exist in your Refiner project, otherwise
 *   forms will silently fail to appear. Known safe defaults: `"en"`, `"nl"`.
 * - Example:
 *    ```ts
 *    const { setLocale } = useRefiner({ projectId: 'xxxx-xxxx', locale: 'en' })
 *    setLocale('nl') // Switch to Dutch for all subsequent forms
 *    ```
 *
 * Notes:
 * - If your form recurrence is set to `"One response"`, it will not show again
 *   to the same browser after the first completion. (Refiner Project → Targeting → Recurrence)
 * - Avoid passing empty objects as `user` — they will be ignored.
 * - Extra metadata can be passed via `showRefinerForm` to enrich responses.
 */

interface UserInfo {
  userId?: string
  userEmail?: string
  userName?: string
  locale?: string
}

interface UseRefinerOptions {
  projectId: string
  locale?: string
  user?: UserInfo
}

export const useRefiner = ({
  projectId,
  locale = 'nl',  // TODO: Currently refiner sets 'nl' as default, 'en' might be missing. We'd prefer to use 'en' as default.
  user
}: UseRefinerOptions) => {
  const currentProjectId = ref(projectId)
  const currentLocale = ref(locale)

  _refiner('setProject', currentProjectId.value)
  _refiner('setLocale', currentLocale.value)

  const setProject = (id: string) => {
    currentProjectId.value = id
    _refiner('setProject', id)
  }

  const setLocale = (locale: string) => {
    const shortLocale = locale.length > 2 ? locale.substring(0, 2) : locale
    currentLocale.value = shortLocale
    _refiner('setLocale', shortLocale)
  }

  const identifyCurrentUser = (userInfo: UserInfo) => {
    if (!userInfo?.userId && !userInfo?.userEmail) return
    _refiner('setLocale', userInfo.locale ?? currentLocale.value)
    _refiner('identifyUser', {
      id: userInfo.userId,
      email: userInfo.userEmail ?? 'anonymous@unknown.com',
      name: userInfo.userName,
      environment: import.meta.env.VITE_MODE
    })
  }

  // Identify immediately if user info provided
  if (user && (user.userId || user.userEmail)) {
    identifyCurrentUser(user)
  }

  const showRefinerForm = (params: {
    formId: string
    locale?: string
    user?: UserInfo
    userRoles?: string[]
    appVersion?: string
    marketCountryCode?: string
    locationNumber?: string
    route?: string
    isMobile?: boolean
  }) => {
    _refiner('setLocale', params.locale ?? currentLocale.value)

    if (params.user && (params.user.userId || params.user.userEmail)) {
      identifyCurrentUser(params.user)
    }

    _refiner('showForm', params.formId)
    _refiner('addToResponse', {
      market_country: params.marketCountryCode,
      location_number: params.locationNumber,
      current_route_page: params.route,
      app_version: params.appVersion,
      user_roles: params.userRoles,
      is_mobile: params.isMobile ?? false,
      user_agent: navigator.userAgent,
      environment: import.meta.env.VITE_MODE // this might be needed to become a param?
    })
  }

  const trackEvent = (eventName: string, payload?: Record<string, never>) => {
    if (!eventName) return
    _refiner('trackEvent', eventName, payload)
  }

  return { setProject, setLocale, identifyCurrentUser, showRefinerForm, trackEvent }
}
